import React from 'react';
import type { FocusEvent, KeyboardEvent } from 'react';

import { css } from '@emotion/react';
import { colors, Primitive } from '@rtkwlf/fenrir-react';

import type { SubMenuItem } from '@packages/components/navigation/navigation.constants';
import { EventKey, LEFT_MENU_WIDTH } from 'ae-constants/constants';

import { LeftMenuItem } from './left-menu-item/left-menu-item';

/**
 *
 * @param event current Event the user trigger
 * @param key the dataset key of the data
 * @returns the value of the key or undefined
 */

const getEventDataSetValue = (
  event: FocusEvent,
  key: string,
): string | undefined => {
  let currentNode = event.target as HTMLElement;
  while (currentNode && currentNode.dataset && !currentNode.dataset[key]) {
    currentNode = currentNode.parentElement as HTMLElement;
  }

  return currentNode.dataset[key];
};

export type LeftMenuProps = {
  items: SubMenuItem[];
};

export const LeftMenu: React.FC<LeftMenuProps> = ({ items }) => {
  const [isFocus, setIsFocus] = React.useState(true);
  const wrapRef = React.useRef<HTMLUListElement>(null);

  const itemsCount = items.length - 1;
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);

  const focusNextItem = () => {
    if (highlightedIndex === itemsCount) {
      setHighlightedIndex(0);
    } else {
      setHighlightedIndex(highlightedIndex + 1);
    }
  };

  const focusPrevItem = () => {
    if (highlightedIndex === 0) {
      setHighlightedIndex(itemsCount);
    } else {
      setHighlightedIndex(highlightedIndex - 1);
    }
  };

  const handleKeypress = (e: KeyboardEvent) => {
    if (e !== null) {
      const node = e.target as Node;
      const linkElm = node.firstChild as HTMLElement;
      if (linkElm !== null) {
        linkElm.click();
      }
    }
  };

  const handleFocusMenu = (e: FocusEvent) => {
    const key = 'index';
    const index = getEventDataSetValue(e, key);
    if (index !== undefined) {
      if (index === '0') {
        setIsFocus(true);
      }
      const currentIndex = parseInt(index, 10);
      setHighlightedIndex(currentIndex);
    }
  };

  React.useEffect(() => {
    if (wrapRef.current) {
      const nodes = wrapRef.current.childNodes;

      if (highlightedIndex >= 0 && highlightedIndex < nodes.length) {
        const currentElm = wrapRef.current.querySelector<HTMLElement>(
          `[data-index="${highlightedIndex}"]`,
        );

        currentElm?.focus();
      }
    }
  }, [highlightedIndex]);

  const handleKeyEvents = (event: KeyboardEvent): void => {
    if (isFocus) {
      switch (event.key) {
        case EventKey.Down: {
          event.preventDefault();
          focusNextItem();
          break;
        }
        case EventKey.Up: {
          event.preventDefault();
          focusPrevItem();
          break;
        }
        case EventKey.Esc: {
          event.preventDefault();
          break;
        }
        case EventKey.Tab: {
          setIsFocus(false);
          break;
        }
        case EventKey.Return:
          handleKeypress(event);
          break;
        default:
          break;
      }
    }
  };

  return items.length > 0 ? (
    <Primitive.nav
      onKeyDown={handleKeyEvents}
      height="full"
      position="sticky"
      top="0"
      bottom="0"
      paddingTop="xxlarge"
      css={css({
        '@media print': { display: 'none' },
        backgroundColor: `rgb(${colors.background.widget.default})`,
        minWidth: LEFT_MENU_WIDTH,
      })}
      ref={wrapRef}
      data-testid="left-menu"
    >
      {items.map((item, index) => (
        <LeftMenuItem
          key={item.to}
          index={index}
          onFocus={handleFocusMenu}
          item={item}
        />
      ))}
    </Primitive.nav>
  ) : null;
};
